/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React from 'react';
import styled from 'styled-components';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus as theme } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { classNames } from 'primereact/utils';
import { useStoreContext } from '@/contexts/StoreContext';
import { useQuery } from '@tanstack/react-query';
import { AntflowApiEndpoint } from '@/services/dataprovider';
import { Skeleton } from 'primereact/skeleton';

interface Props {
  className?: string;
  markdown: string;
  removeImages?: boolean;
  style?: { [key: string]: React.CSSProperties };
}

const Div = styled.div`
  p {
    margin: 6px 0;
  }
`;

const stripMarkdownMetadata = (markdown: string): string => {
  const withoutMetadata = markdown.replace(/^---\n([\s\S]*?)\n---\n/, '');
  const withoutImages = withoutMetadata.replace(/!\[([^\]]*)\]\(([^)]*)\)/g, '');
  return withoutImages;
};

const Image = ({ alt, src }: { alt?: string; src?: string }) => {
  const { antflowProcessApiEndpoint } = useStoreContext();
  const { data: image, isFetching } = useQuery({
    queryKey: ['image', alt, src],
    enabled: !!src,
    queryFn: async () => {
      if (!src) {
        return null;
      }
      const url = new URL(src);
      if (url.hostname !== window.location.hostname) {
        return src;
      }
      const endpoint = antflowProcessApiEndpoint as AntflowApiEndpoint;
      return await endpoint.get_image(src).catch(() => null);
    },
    refetchOnWindowFocus: false
  });
  if (isFetching) {
    return <Skeleton width="full" height="10rem"></Skeleton>;
  }
  return <img src={image ?? src} alt={alt} style={{ maxWidth: '100%' }} />;
};

export const CMarkdown: React.FC<Props> = ({ markdown, style, className, removeImages = true }) => {
  const cleanMarkdown = removeImages ? stripMarkdownMetadata(markdown) : markdown;

  return (
    <Div className={classNames(className)}>
      <Markdown
        className="text-left"
        remarkPlugins={[remarkGfm]}
        children={cleanMarkdown}
        components={{
          h1(props) {
            const { children, className } = props;
            return <h3 className={className}>{children}</h3>;
          },
          h2(props) {
            const { children, className } = props;
            return <h4 className={className}>{children}</h4>;
          },
          h3(props) {
            const { children, className } = props;
            return <h4 className={className}>{children}</h4>;
          },
          h4(props) {
            const { children, className } = props;
            return <h5 className={className}>{children}</h5>;
          },
          img(props) {
            const { alt, src } = props;
            return <Image alt={alt} src={src} />;
          },
          code(props) {
            const { children, className } = props;
            const match = /language-(\w+)/.exec(className || '');
            return match ? (
              <SyntaxHighlighter
                PreTag="div"
                children={String(children).replace(/\n$/, '')}
                language={match[1]}
                style={style ?? theme}
              />
            ) : (
              <code className={className}>{children}</code>
            );
          }
        }}
      />
    </Div>
  );
};
